import { request } from "./api";
import type {
  PilotCatalog,
  PilotEngagements,
  PilotRegistration,
  PilotReservation,
} from "./place-pilot-types";
import type { ReservationDraft } from "./view-models";
export interface ReservationCommand {
  space_id: string;
  starts_at: string;
  ends_at: string;
  purpose: string;
  conditions_version: string;
}
/** Preserve the exact uncertain command across reloads, even after republication. */
export function pendingReservationCommand(
  draft: ReservationDraft,
): { body: ReservationCommand; key: string } | null {
  if (
    !draft.request_key ||
    !/^[A-Za-z0-9_-]{16,120}$/.test(draft.request_key) ||
    !draft.request_fingerprint
  )
    return null;
  try {
    const body: unknown = JSON.parse(draft.request_fingerprint);
    if (!body || typeof body !== "object" || Array.isArray(body)) return null;
    const b = body as Record<string, unknown>;
    if (
      Object.keys(b).sort().join() !==
        [
          "conditions_version",
          "ends_at",
          "purpose",
          "space_id",
          "starts_at",
        ].join() ||
      typeof b.space_id !== "string" ||
      !/^[a-f0-9-]{36}$/.test(b.space_id) ||
      typeof b.purpose !== "string" ||
      b.purpose.length > 1000 ||
      typeof b.conditions_version !== "string" ||
      !/^sha256:[a-f0-9]{64}$/.test(b.conditions_version) ||
      typeof b.starts_at !== "string" ||
      typeof b.ends_at !== "string" ||
      !Number.isFinite(Date.parse(b.starts_at)) ||
      !Number.isFinite(Date.parse(b.ends_at)) ||
      Date.parse(b.ends_at) <= Date.parse(b.starts_at)
    )
      return null;
    return { body: b as unknown as ReservationCommand, key: draft.request_key };
  } catch {
    return null;
  }
}
const base = "/place-pilot";
export const pilotApi = {
  catalog: () => request<PilotCatalog>(base + "/catalog"),
  engagements: () => request<PilotEngagements>(base + "/engagements"),
  register: (id: string, key: string) =>
    request<PilotRegistration>(
      `${base}/events/${encodeURIComponent(id)}/register`,
      "POST",
      {},
      key,
    ),
  cancelRegistration: (id: string, key: string) =>
    request(
      `${base}/registrations/${encodeURIComponent(id)}/cancel`,
      "POST",
      {},
      key,
    ),
  reserve: (body: ReservationCommand, key: string) =>
    request<PilotReservation>(base + "/reservations", "POST", body, key),
  cancelReservation: (id: string, key: string) =>
    request(
      `${base}/reservations/${encodeURIComponent(id)}/cancel`,
      "POST",
      {},
      key,
    ),
};
export const engagementStatus: Record<string, string> = {
  confirmed: "Confirmée",
  pending: "Demande en attente",
  declined: "Refusée",
  cancelled: "Annulée",
};
export function pilotTime(value: string, timezone = "Europe/Paris") {
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(value));
}
